import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { DRAWER_TABS, MENU_HEIGHT, PHONE_TAB_BAR_HEIGHT, Z_INDEX } from '../constants'
import Drawer from './Drawer'
import Graph from './Graph'
import GraphBoundary from './GraphBoundary'
import PhoneTabBar from './PhoneTabBar'
import useGraphView from '../models/useGraphView'
import { isDetailPath, type PhoneView } from '../utilities/nativeApp'

type PhoneShellProps = {
  /** What the drawer shows: the page of the Nodes, Networks or Account tab. */
  drawer: ReactNode
  /** Everything else of the layout: the routed windows, the terminals, search. */
  children?: ReactNode
}

/**
 * The noBGP app on a phone (Layout.tsx, lib/nativeApp.ts): three views chosen in a bar at the
 * bottom. List is the drawer, full screen. Graph and Map are the console's own canvas, the graph
 * engine or the map engine (`useGraphView.mapView`), with no drawer over it.
 *
 * ⚠ A tap on a node in the graph or the map opens its page, which lives in the drawer: so a move
 * to a node, network or account PAGE switches to List. A tap on the empty canvas goes to `/`, which
 * is not a page, and stays on the canvas; only List turns `/` into Nodes.
 */
export default function PhoneShell({ drawer, children }: Readonly<PhoneShellProps>) {
  const location = useLocation()
  const navigate = useNavigate()
  const setMapView = useGraphView(s => s.setMapView)
  const [view, setView] = useState<PhoneView>('list')
  const lastPath = useRef(location.pathname)

  useEffect(() => {
    if (location.pathname === lastPath.current) return
    lastPath.current = location.pathname
    if (view !== 'list' && isDetailPath(location.pathname)) setView('list')
  }, [location.pathname, view])

  // List shows a page, and `/` is none: it becomes Nodes. ⚠ An effect on every arrival at `/`, not
  // a <Navigate>, which fires once when it mounts: the app's start goes to `/` again after it
  // (sign-in, the org load), and the list then stayed empty until another tab was chosen.
  useEffect(() => {
    if (view === 'list' && location.pathname === '/') navigate('/nodes', { replace: true })
  }, [view, location.pathname, location.key, navigate])

  const choose = (next: PhoneView) => {
    if (next === 'graph') setMapView(false)
    if (next === 'map') setMapView(true)
    setView(next)
  }

  return (
    <>
      {view === 'list' ? (
        <Drawer fullScreen>{drawer}</Drawer>
      ) : (
        <>
          <GraphBoundary>
            <Graph bottomInset={`calc(${PHONE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`} />
          </GraphBoundary>
          <PhoneTopTabs onPick={() => setView('list')} />
        </>
      )}
      {children}
      <PhoneTabBar view={view} onChange={choose} />
    </>
  )
}

/**
 * Nodes, Networks and Account over the graph and the map, where the list has them at its top: the
 * bar stays put when the view changes. A tab opens its list.
 */
function PhoneTopTabs({ onPick }: Readonly<{ onPick: () => void }>) {
  const location = useLocation()
  const active = DRAWER_TABS.findIndex(tab => location.pathname.startsWith(tab.path))
  return (
    <Paper
      elevation={0}
      sx={({ palette, shape }) => ({
        position: 'absolute',
        top: MENU_HEIGHT,
        left: 0,
        width: '100%',
        borderRadius: 0,
        background: palette.transparent.light,
        backdropFilter: shape.backdropBlur,
        zIndex: Z_INDEX.DRAWER,
      })}
    >
      <Tabs value={active === -1 ? false : active} variant="fullWidth">
        {DRAWER_TABS.map(tab => (
          <Tab key={tab.path} label={tab.label} component={Link} to={tab.path} onClick={onPick} />
        ))}
      </Tabs>
    </Paper>
  )
}
